import { create } from "zustand";

import {
  api,
  isNetworkError,
  NETWORK_FAILURE_MESSAGE,
  SESSION_EXPIRED_EVENT,
  setCsrfToken,
} from "../api/client";
import { useNavigationStore } from "./navigationStore";

const OFFLINE_MAIN_MENU_ACTIVATED_KEY = "voice-companion.session.was_activated";

type SessionState = {
  activated: boolean;
  loading: boolean;
  offlineMainMenuShell: boolean;
  error: string;
  init: () => Promise<void>;
  activate: (token: string) => Promise<void>;
};

function rememberActivatedDevice(activated: boolean) {
  if (activated) {
    localStorage.setItem(OFFLINE_MAIN_MENU_ACTIVATED_KEY, "true");
    return;
  }
  localStorage.removeItem(OFFLINE_MAIN_MENU_ACTIVATED_KEY);
}

function hasActivatedDeviceForOfflineShell(): boolean {
  return localStorage.getItem(OFFLINE_MAIN_MENU_ACTIVATED_KEY) === "true";
}

export const useSessionStore = create<SessionState>((set) => ({
  activated: false,
  loading: true,
  offlineMainMenuShell: false,
  error: "",
  async init() {
    try {
      const session = await api.sessionMe();
      setCsrfToken(session.csrf_token ?? "");
      rememberActivatedDevice(session.activated);
      set({
        activated: session.activated,
        loading: false,
        offlineMainMenuShell: false,
        error: "",
      });
    } catch (error) {
      setCsrfToken("");
      if (isNetworkError(error) && hasActivatedDeviceForOfflineShell()) {
        useNavigationStore.getState().home();
        set({
          activated: true,
          loading: false,
          offlineMainMenuShell: true,
          error: NETWORK_FAILURE_MESSAGE,
        });
        return;
      }
      set({
        activated: false,
        loading: false,
        offlineMainMenuShell: false,
        error: error instanceof Error ? error.message : "初始化失败。",
      });
    }
  },
  async activate(token) {
    await api.activate(token);
    const session = await api.sessionMe();
    setCsrfToken(session.csrf_token ?? "");
    rememberActivatedDevice(session.activated);
    set({
      activated: session.activated,
      offlineMainMenuShell: false,
      error: "",
    });
  },
}));

window.addEventListener(SESSION_EXPIRED_EVENT, (event) => {
  const detail = (event as CustomEvent<{ message?: string }>).detail;
  localStorage.removeItem(OFFLINE_MAIN_MENU_ACTIVATED_KEY);
  useNavigationStore.getState().home();
  useSessionStore.setState({
    activated: false,
    loading: false,
    offlineMainMenuShell: false,
    error: detail?.message ?? "激活已经失效，请重新输入激活码。",
  });
});
